class EEVariantSelects extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('change', this.onVariantChange);
    this.addEventListener('click', this.onVariantClick);
    
    // Initialize by checking for URL params
    this.init();
  }
  
  init() {
    // If there's a variant param in the URL, ensure it's selected
    const searchParams = new URLSearchParams(window.location.search);
    const variantId = searchParams.get('variant');
    
    if (variantId) {
      // Try to find the variant options from hidden JSON
      const variantData = this.querySelector('[data-selected-variant]');
      if (variantData) {
        try {
          // First try to parse as JSON
          const variant = JSON.parse(variantData.textContent);
          if (variant && variant.id && variant.id.toString() === variantId) {
            // Make sure this variant is selected
            this.updateSelectors(variant.options);
          }
        } catch (e) {
          console.warn('Error parsing variant data with JSON.parse, trying alternative approach', e);
          
          // If JSON parsing fails, try to find the variant by ID from the inputs
          const variantInput = this.querySelector(`input[data-variant-id="${variantId}"]`);
          if (variantInput) {
            variantInput.checked = true;
            
            // Update card styling for this input
            const cardContainer = variantInput.closest('.config-card-container');
            if (cardContainer) {
              const fieldset = cardContainer.closest('fieldset');
              if (fieldset) {
                fieldset.querySelectorAll('.config-card-container').forEach(card => {
                  card.classList.remove('selected');
                });
              }
              cardContainer.classList.add('selected');
            }
          }
        }
      } else {
        // If there's no variant data element, try to find the variant by ID from the inputs
        const variantInput = this.querySelector(`input[data-variant-id="${variantId}"]`);
        if (variantInput) {
          variantInput.checked = true;
          
          // Update card styling for this input
          const cardContainer = variantInput.closest('.config-card-container');
          if (cardContainer) {
            const fieldset = cardContainer.closest('fieldset');
            if (fieldset) {
              fieldset.querySelectorAll('.config-card-container').forEach(card => {
                card.classList.remove('selected');
              });
            }
            cardContainer.classList.add('selected');
          }
        }
      }
    }
  }
  
  updateSelectors(options) {
    if (!options || !options.length) return;
    
    // Update all selects
    const selects = this.querySelectorAll('select');
    selects.forEach((select, index) => {
      if (options[index]) {
        select.value = options[index];
      }
    });
    
    // Update all radio buttons
    const radioGroups = this.querySelectorAll('fieldset.product-form__input');
    radioGroups.forEach((fieldset, index) => {
      if (options[index]) {
        const radio = fieldset.querySelector(`input[type="radio"][value="${options[index]}"]`);
        if (radio) {
          radio.checked = true;
        }
      }
    });
  }

  onVariantClick(event) {
    const target = event.target;
    
    // If a radio button's label was clicked, find the associated radio input
    if (target.tagName === 'LABEL' && target.htmlFor) {
      const radio = document.getElementById(target.htmlFor);
      if (radio && radio.type === 'radio' && !radio.checked) {
        radio.checked = true;
        
        // Manually dispatch a change event
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
    
    // If a swatch was clicked and it's inside a label, find the label and simulate a click
    if (target.classList.contains('swatch') || target.closest('.swatch')) {
      const swatch = target.classList.contains('swatch') ? target : target.closest('.swatch');
      const label = swatch.closest('label');
      if (label && label.htmlFor) {
        const radio = document.getElementById(label.htmlFor);
        if (radio && radio.type === 'radio' && !radio.checked) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }
    
    // Handle clicks on configurator card elements
    const cardElement = target.closest('.config-card-container');
    if (cardElement) {
      const input = cardElement.querySelector('input');
      if (input && !input.checked && !input.disabled) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        
        // Update visual state for all cards in the same fieldset
        const fieldset = cardElement.closest('fieldset');
        if (fieldset) {
          const allCards = fieldset.querySelectorAll('.config-card-container');
          allCards.forEach(card => {
            card.classList.remove('selected');
          });
          cardElement.classList.add('selected');
        }
      }
    }
  }

  onVariantChange(event) {
    const target = this.getInputForEventTarget(event.target);
    this.updateSelectionMetadata(event);
    
    // Check if this is a configurator card input
    const cardInput = (event.target.tagName === 'INPUT' && event.target.closest('.config-card-container'));
    
    // If we have a data-variant-id attribute from a configurator card, use that for the variant
    if (cardInput && event.target.dataset.variantId) {
      // Find the matching variant
      const variantId = event.target.dataset.variantId;
      
      // Dispatch the custom event with the variant ID
      this.dispatchEvent(new CustomEvent('ee-variant-change', {
        detail: {
          event,
          target,
          variantId: variantId,
          selectedOptionValues: this.selectedOptionValues,
          isConfiguratorCard: true
        },
        bubbles: true
      }));
    } else {
      // Standard dispatch for regular variant selectors
      this.dispatchEvent(new CustomEvent('ee-variant-change', {
        detail: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues
        },
        bubbles: true
      }));
    }
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        ?.removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const productFormInput = target.closest('.product-form__input');
      if (productFormInput) {
        const selectedDropdownSwatchValue = productFormInput.querySelector('[data-selected-value] > .swatch');
        if (!selectedDropdownSwatchValue) return;
        if (swatchValue) {
          selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
          selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
        } else {
          selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
          selectedDropdownSwatchValue.classList.add('swatch--unavailable');
        }

        selectedDropdownSwatchValue.style.setProperty(
          '--swatch-focal-point',
          target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
        );
      }
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const productFormInput = target.closest('.product-form__input');
      if (productFormInput) {
        const selectedSwatchValue = productFormInput.querySelector('[data-selected-value]');
        if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
      }
      
      // Update card selection visual state
      const cardContainer = target.closest('.config-card-container');
      if (cardContainer) {
        // First remove selected class from all sibling cards
        const fieldset = cardContainer.closest('fieldset');
        if (fieldset) {
          fieldset.querySelectorAll('.config-card-container').forEach(card => {
            card.classList.remove('selected');
          });
        }
        
        // Add selected class to the current card
        cardContainer.classList.add('selected');
      }
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('ee-variant-selects', EEVariantSelects); 